import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllPresentations, deletePresentation } from '../../../services/mentiService';
import { createMentiSession, deleteMentiSession } from '../../../services/mentiSessionService';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 6) return 'God natt';
  if (hour < 12) return 'God morgon';
  if (hour < 18) return 'God ettermiddag';
  return 'God kveld';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('nn-NO', { day: 'numeric', month: 'short', year: 'numeric' });
}

const PREVIEW_COUNT = 3;

export default function MentiDashboard({ onCreateNew, onEdit, onStartSession }) {
  const [presentations, setPresentations] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [sessionFilter, setSessionFilter] = useState('alle');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const list = await getAllPresentations();
      setPresentations(list);
    } catch (error) {
      console.error('Error loading presentations:', error);
    }
    try {
      const sessionsSnap = await getDocs(collection(db, 'mentiSessions'));
      const sessionList = [];
      sessionsSnap.forEach(d => sessionList.push({ id: d.id, ...d.data() }));
      setSessions(sessionList);
    } catch (error) {
      console.error('Error loading menti sessions:', error);
    }
    setLoading(false);
  };

  const handleStartSession = async (presentationId) => {
    try {
      const session = await createMentiSession(presentationId);
      onStartSession(session.id);
    } catch (error) {
      console.error('Error creating menti session:', error);
      alert('Kunne ikkje opprette Menti-okt. Prov igjen.');
    }
  };

  const handleDeletePresentation = async (presentation) => {
    const confirmed = window.confirm(`Er du sikker pa at du vil slette "${presentation.title}"? Dette kan ikkje angrast.`);
    if (!confirmed) return;

    try {
      await deletePresentation(presentation.id);
      setPresentations(presentations.filter(p => p.id !== presentation.id));
    } catch (error) {
      console.error('Error deleting presentation:', error);
      alert('Kunne ikkje slette presentasjonen. Prov igjen.');
    }
  };

  const handleDeleteSession = async (e, session) => {
    e.stopPropagation();
    const confirmed = window.confirm(`Slett okt med PIN ${session.id}? Dette kan ikkje angrast.`);
    if (!confirmed) return;

    try {
      await deleteMentiSession(session.id);
      setSessions(sessions.filter(s => s.id !== session.id));
    } catch (error) {
      console.error('Error deleting menti session:', error);
      alert('Kunne ikkje slette okta. Prov igjen.');
    }
  };

  const filteredPresentations = presentations.filter(p =>
    p.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const displayedPresentations = searchQuery || showAll
    ? filteredPresentations
    : filteredPresentations.slice(0, PREVIEW_COUNT);

  const hasMore = !searchQuery && filteredPresentations.length > PREVIEW_COUNT;

  const totalSlides = presentations.reduce((sum, p) => sum + (p.slides?.length || 0), 0);

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-background-light font-display text-[#0d121b] pb-28 overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-30 flex items-center bg-background-light/95 backdrop-blur-sm p-4 justify-between transition-colors duration-300">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex size-12 shrink-0 items-center justify-center rounded-full bg-white p-1 shadow-ambient hover:shadow-ambient-lg transition-shadow" title="Tilbake til startsida">
            <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-full size-10 flex items-center justify-center">
              <span className="material-symbols-outlined text-white text-xl">arrow_back</span>
            </div>
          </Link>
          <div>
            <p className="text-sm font-medium text-gray-500 leading-none">{getGreeting()},</p>
            <h2 className="text-[#0d121b] text-xl font-bold leading-tight">Mikkel Menti</h2>
          </div>
        </div>
      </header>

      {/* Search Bar */}
      <div className="px-4 py-2">
        <div className="group flex w-full flex-1 items-center rounded-2xl bg-white shadow-ambient ghost-border ghost-border-focus transition-all duration-200">
          <div className="text-blue-600 flex items-center justify-center pl-4">
            <span className="material-symbols-outlined">search</span>
          </div>
          <input
            type="text"
            className="flex w-full min-w-0 flex-1 resize-none bg-transparent py-4 px-3 text-[#0d121b] placeholder:text-[#9ca3af] focus:outline-none border-none text-base font-medium"
            placeholder="Finn ein presentasjon..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Stats Section */}
      <div className="px-4 py-4">
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <div className="flex flex-col gap-1 rounded-2xl p-4 sm:p-5 bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/20 relative overflow-hidden group">
            <div className="absolute top-0 right-0 -mr-4 -mt-4 h-24 w-24 rounded-full bg-white/10 blur-xl group-hover:scale-150 transition-transform duration-500"></div>
            <div className="flex items-center gap-2 text-white/90">
              <span className="material-symbols-outlined text-[20px]">slideshow</span>
              <p className="text-sm font-medium leading-normal">Presentasjonar</p>
            </div>
            <p className="text-3xl font-extrabold leading-tight tracking-tight mt-2">
              {presentations.length}
            </p>
          </div>

          <div className="flex flex-col gap-1 rounded-2xl p-4 sm:p-5 bg-white shadow-ambient relative overflow-hidden">
            <div className="flex items-center gap-2 text-blue-600">
              <span className="material-symbols-outlined text-[20px]">layers</span>
              <p className="text-sm font-medium leading-normal">Totalt lysbilete</p>
            </div>
            <p className="text-[#0d121b] text-3xl font-extrabold leading-tight tracking-tight mt-2">
              {totalSlides}
            </p>
          </div>
        </div>
      </div>

      {/* Section Header */}
      <div className="flex items-center justify-between px-4 pt-2 pb-2">
        <h3 className="text-[#0d121b] text-xl font-bold leading-tight tracking-tight">Mine presentasjonar</h3>
        {hasMore && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-blue-600 text-sm font-bold hover:text-blue-500 transition-colors"
          >
            {showAll ? 'Vis faerre' : 'Vis alle'}
          </button>
        )}
      </div>

      {/* Presentation Feed */}
      <div className="flex flex-col gap-4 px-4 pb-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500 font-medium">Lastar presentasjonar...</div>
          </div>
        ) : filteredPresentations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <span className="material-symbols-outlined text-6xl text-gray-300 mb-4">slideshow</span>
            <h4 className="text-lg font-bold text-gray-600 mb-2">
              {searchQuery ? 'Fann ingen presentasjonar' : 'Ingen presentasjonar enno'}
            </h4>
            <p className="text-gray-500 mb-4">
              {searchQuery ? 'Prov eit anna sokeord' : 'Lag din fyrste presentasjon for a kome i gang!'}
            </p>
            {!searchQuery && (
              <button
                onClick={onCreateNew}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold hover:brightness-110 transition-all"
              >
                Lag presentasjon
              </button>
            )}
          </div>
        ) : (
          displayedPresentations.map((presentation) => (
            <PresentationCard
              key={presentation.id}
              presentation={presentation}
              onStartSession={handleStartSession}
              onEdit={onEdit}
              onDelete={handleDeletePresentation}
            />
          ))
        )}
      </div>

      {/* Sessions */}
      {sessions.length > 0 && (() => {
        const filteredSessions = sessions.filter(s => {
          if (sessionFilter === 'alle') return true;
          if (sessionFilter === 'aktiv') return s.status === 'active';
          if (sessionFilter === 'ventar') return s.status === 'waiting';
          if (sessionFilter === 'avslutta') return s.status === 'finished';
          return true;
        });

        const sessionFilterOptions = [
          { key: 'alle', label: 'Alle' },
          { key: 'aktiv', label: 'Aktiv' },
          { key: 'ventar', label: 'Ventar' },
          { key: 'avslutta', label: 'Avslutta' },
        ];

        return (
          <>
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <h3 className="text-[#0d121b] text-xl font-bold leading-tight tracking-tight">Okter</h3>
            </div>

            {/* Status Filter Pills */}
            <div className="flex items-center gap-2 px-4 pb-3 overflow-x-auto">
              {sessionFilterOptions.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setSessionFilter(opt.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-bold whitespace-nowrap transition-colors duration-200 ${
                    sessionFilter === opt.key
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-3 px-4 pb-4">
              {filteredSessions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">filter_list</span>
                  <p className="text-gray-500 text-sm">Ingen okter med status &laquo;{sessionFilterOptions.find(o => o.key === sessionFilter)?.label}&raquo;</p>
                </div>
              ) : (
                filteredSessions.map((session) => {
                  const pres = presentations.find(p => p.id === session.presentationId);
                  const presTitle = pres?.title || 'Ukjend presentasjon';
                  const participantCount = session.participants ? Object.keys(session.participants).length : 0;
                  const statusLabel = session.status === 'waiting' ? 'Ventar' : session.status === 'active' ? 'Aktiv' : 'Avslutta';
                  const statusColor = session.status === 'active' ? 'bg-green-100 text-green-700' : session.status === 'waiting' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600';
                  const borderColor = session.status === 'active' ? 'border-l-blue-500' : session.status === 'waiting' ? 'border-l-yellow-400' : 'border-l-gray-300';

                  return (
                    <div
                      key={session.id}
                      onClick={() => onStartSession(session.id)}
                      className={`bg-white rounded-2xl border-l-4 ${borderColor} shadow-ambient p-4 hover:shadow-lg transition-shadow text-left w-full cursor-pointer`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-[#0d121b] truncate flex-1">{presTitle}</h4>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold px-2 py-1 rounded-full ${statusColor}`}>{statusLabel}</span>
                          <button
                            onClick={(e) => handleDeleteSession(e, session)}
                            className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-lg hover:bg-red-50"
                            title="Slett okt"
                          >
                            <span className="material-symbols-outlined text-[18px]">delete</span>
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-[16px]">tag</span>
                          <span>PIN: {session.id}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-[16px]">group</span>
                          <span>{participantCount} deltakarar</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-[16px]">layers</span>
                          <span>Slide {(session.currentSlideIndex || 0) + 1}</span>
                        </div>
                        <span className="ml-auto">{formatDate(session.createdAt)}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        );
      })()}

      {/* Floating Action Button */}
      <div className="fixed bottom-6 right-6 z-40">
        <button
          onClick={onCreateNew}
          className="group flex items-center justify-center size-14 hover:w-auto hover:pl-5 hover:pr-6 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 hover:brightness-110 transition-all duration-300 ease-out overflow-hidden"
        >
          <span className="material-symbols-outlined text-[26px] shrink-0 group-hover:rotate-90 transition-transform duration-300">add</span>
          <span className="max-w-0 overflow-hidden opacity-0 group-hover:max-w-48 group-hover:opacity-100 group-hover:ml-2 transition-all duration-300 ease-out text-sm font-bold whitespace-nowrap">Lag ny presentasjon</span>
        </button>
      </div>
    </div>
  );
}

function PresentationCard({ presentation, onStartSession, onEdit, onDelete }) {
  const slideCount = presentation.slides?.length || 0;

  return (
    <div className="bg-white rounded-2xl shadow-ambient overflow-hidden hover:shadow-lg transition-shadow duration-200">
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h4 className="text-[#0d121b] text-lg font-bold truncate">{presentation.title}</h4>
            {presentation.description && (
              <p className="text-gray-500 text-sm mt-0.5 line-clamp-1">{presentation.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
          <div className="flex items-center gap-1">
            <span className="material-symbols-outlined text-[16px]">layers</span>
            <span>{slideCount} {slideCount === 1 ? 'lysbilde' : 'lysbilete'}</span>
          </div>
          <div className="flex items-center gap-1 ml-auto">
            <span className="material-symbols-outlined text-[16px]">schedule</span>
            <span>{formatDate(presentation.updatedAt)}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onStartSession(presentation.id)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:brightness-110 text-white rounded-xl font-bold text-sm transition-all"
          >
            <span className="material-symbols-outlined text-[18px]">play_arrow</span>
            Start okt
          </button>
          <button
            onClick={() => onEdit(presentation)}
            className="flex items-center justify-center size-10 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
            title="Rediger"
          >
            <span className="material-symbols-outlined text-[18px]">edit</span>
          </button>
          <button
            onClick={() => onDelete(presentation)}
            className="flex items-center justify-center size-10 rounded-xl bg-gray-100 hover:bg-red-50 text-gray-600 hover:text-red-600 transition-colors"
            title="Slett"
          >
            <span className="material-symbols-outlined text-[18px]">delete</span>
          </button>
        </div>
      </div>
    </div>
  );
}
